import {structuredPatch} from 'diff'
import type {StructuredPatchOptionsAbortable} from 'diff'

/** Bounds `structuredPatch`'s diffing time so one large, divergent file can't block the main
 * thread indefinitely — `timeout` aborts reliably regardless of input size, unlike `maxEditLength`. */
const DIFF_TIMEOUT_MS = 1500

/** jsdiff's own `ignoreWhitespace` option only trims each line's leading/trailing whitespace
 * before comparing, so e.g. `const x = 1;` vs `const x=1;` would still show as changed. This
 * strips all whitespace before comparing, matching the "hide whitespace" affordance users expect
 * from GitHub's web diff view, while leaving the original line text untouched for display. */
function normalizeWhitespaceForComparison(line: string): string {
  return line.replace(/\s+/g, '')
}

/** jsdiff's line-diffing implementation honors `comparator` (it's handled generically in its
 * shared base `Diff.equals`), but the public types only declare it for `diffArrays`. This narrowly
 * re-adds it to `structuredPatch`'s options so we can plug in whitespace-insensitive comparison
 * without an untyped `any`. */
type StructuredPatchOptionsWithComparator = StructuredPatchOptionsAbortable & {
  comparator?: (a: string, b: string) => boolean
}

/** Produces a header-less unified diff (starting at `@@`) for a single file, matching
 * `github-app`'s `remoteDiffAdapter.ts`. Returns `undefined` if identical or timed out. */
export function generateUnifiedPatch(
  oldPath: string,
  newPath: string,
  before: string,
  after: string,
  options: {ignoreWhitespace?: boolean} = {},
): string | undefined {
  if (before === after) return undefined
  const patchOptions: StructuredPatchOptionsWithComparator = {
    context: 3,
    timeout: DIFF_TIMEOUT_MS,
    comparator: options.ignoreWhitespace
      ? (a, b) => normalizeWhitespaceForComparison(a) === normalizeWhitespaceForComparison(b)
      : undefined,
  }
  const patch = structuredPatch(oldPath, newPath, before, after, '', '', patchOptions)
  if (!patch || patch.hunks.length === 0) return undefined
  return patch.hunks
    .map(hunk => {
      const header = `@@ -${hunk.oldStart},${hunk.oldLines} +${hunk.newStart},${hunk.newLines} @@`
      return [header, ...hunk.lines].join('\n')
    })
    .join('\n')
}
